/**
 * One teaching day, assembled from the two things the teaching query knows
 * about: the classes you teach, and the attendance rolls assigned to you.
 *
 * They overlap — a class that takes attendance appears in both — so they merge
 * on the schedule block. The roll is what carries the action and the count, the
 * class is what carries the link to the class itself, and a block can have
 * either, or both. Chapel arrives as a roll with no class; a class that does
 * not take attendance arrives with no roll.
 */
import { WEEKDAYS, type Weekday } from '../Schedules/scheduleUtils';
import type { TeachingAttendanceRoll, TeachingClass } from './teachingContext';

export interface DayMeeting {
  blockId: string;
  title: string;
  /** The course and section, when this block belongs to a class you teach. */
  subtitle?: string;
  startsAt: string;
  endsAt: string;
  timeLabel: string;
  classroom?: string | null;
  /** Present when the block belongs to one of your classes. */
  classOffering?: TeachingClass;
  /** Present when you are the one who takes this roll. */
  roll?: TeachingAttendanceRoll;
}

/** The weekday key for a yyyy-mm-dd string, read as a local date. */
export function weekdayOf(date: string): Weekday | undefined {
  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return undefined;
  return WEEKDAYS[new Date(year, month - 1, day).getDay()];
}

function withinTerm(classOffering: TeachingClass, date: string) {
  const startsOn = classOffering.term?.startsOn ? String(classOffering.term.startsOn) : undefined;
  const endsOn = classOffering.term?.endsOn ? String(classOffering.term.endsOn) : undefined;
  if (startsOn && date < startsOn) return false;
  if (endsOn && date > endsOn) return false;
  return true;
}

function meetsOn(block: TeachingClass['scheduleBlocks'][number], date: string, weekday?: Weekday) {
  if (block.oneTime) return block.oneTimeDate ? String(block.oneTimeDate) === date : false;
  return Boolean(weekday && block.weekdays.includes(weekday));
}

export function buildDay(
  classes: readonly TeachingClass[],
  rolls: readonly TeachingAttendanceRoll[],
  date: string
): DayMeeting[] {
  const weekday = weekdayOf(date);
  const byBlock = new Map<string, DayMeeting>();

  classes.forEach((classOffering) => {
    if (!withinTerm(classOffering, date)) return;
    classOffering.scheduleBlocks.forEach((block) => {
      if (!meetsOn(block, date, weekday)) return;
      byBlock.set(block.id, {
        blockId: block.id,
        title: classOffering.displayName,
        subtitle: [classOffering.course?.name, `Section ${classOffering.section}`].filter(Boolean).join(' · '),
        startsAt: block.startsAt,
        endsAt: block.endsAt,
        timeLabel: block.timeLabel,
        classroom: block.classroom?.name,
        classOffering,
      });
    });
  });

  rolls.forEach((roll) => {
    const blockId = roll.scheduleBlock.id;
    const existing = byBlock.get(blockId);
    byBlock.set(blockId, {
      blockId,
      title: existing?.title ?? roll.scheduleBlock.displayTitle,
      subtitle: existing?.subtitle,
      startsAt: roll.startsAt,
      endsAt: roll.endsAt,
      timeLabel: roll.timeLabel,
      classroom: roll.classroom?.name ?? existing?.classroom,
      classOffering: existing?.classOffering,
      roll,
    });
  });

  return [...byBlock.values()].sort((first, second) =>
    first.startsAt.localeCompare(second.startsAt) || first.title.localeCompare(second.title)
  );
}

/** Where "now" sits in the day: the first meeting that has not ended yet. */
export function nextMeetingIndex(meetings: readonly DayMeeting[], time?: string): number {
  if (!time) return -1;
  return meetings.findIndex((meeting) => meeting.endsAt > time);
}
